import React from 'react';
import { Coins, Lightning, ShoppingCart, X } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { CREDIT_PACKS, PRICING_TIERS } from '../lib/stripe-config';

interface InsufficientCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  required: number;
  balance: number;
}

export function InsufficientCreditsModal({ isOpen, onClose, required, balance }: InsufficientCreditsModalProps) {
  const { createCheckoutSession, user } = useAuth();
  const deficit = required - balance;

  if (!isOpen) return null;

  const handleBuyPack = async (priceId: string) => {
    await createCheckoutSession(priceId, 'payment');
  };

  const handleUpgrade = async (priceId: string | null) => {
    if (!priceId) return;
    await createCheckoutSession(priceId, 'subscription');
  };

  // Find the cheapest pack that covers the deficit
  const suggestedPack = CREDIT_PACKS.find(p => p.credits >= deficit) || CREDIT_PACKS[CREDIT_PACKS.length - 1];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-md mx-4 bg-zinc-900 border border-zinc-700 rounded-xl p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300">
          <X size={18} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <Coins size={22} className="text-red-400" weight="fill" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">Not Enough Credits</h3>
            <p className="text-zinc-400 text-sm">
              Need {required} credits, you have {balance}
            </p>
          </div>
        </div>

        {/* Quick buy */}
        <div className="mb-4">
          <p className="text-zinc-400 text-xs uppercase tracking-wide mb-2">Quick top-up</p>
          <div className="grid grid-cols-3 gap-2">
            {CREDIT_PACKS.map(pack => (
              <button
                key={pack.credits}
                onClick={() => handleBuyPack(pack.priceId)}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
                  pack === suggestedPack
                    ? 'border-yellow-500/50 bg-yellow-500/10 hover:bg-yellow-500/20'
                    : 'border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800'
                }`}
              >
                <span className="text-white font-semibold">{pack.credits}</span>
                <span className="text-zinc-400 text-xs">credits</span>
                <span className="text-yellow-400 text-sm font-medium">{pack.priceLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Subscription upsell */}
        {(!user?.subscription_tier || user.subscription_tier === 'free') && (
          <div className="pt-4 border-t border-zinc-800">
            <p className="text-zinc-400 text-xs uppercase tracking-wide mb-2">Or subscribe for monthly credits</p>
            <div className="space-y-2">
              {PRICING_TIERS.filter(t => t.id !== 'free').map(tier => (
                <button
                  key={tier.id}
                  onClick={() => handleUpgrade(tier.priceId)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Lightning size={16} className="text-yellow-400" weight="fill" />
                    <span className="text-white font-medium">{tier.name}</span>
                    <span className="text-zinc-400 text-sm">{tier.creditsLabel}</span>
                  </div>
                  <span className="text-yellow-400 font-medium">{tier.priceLabel}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
